import { useMemo, useState, type ReactNode } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import ToolTabs from '@/app/common/components/ToolTabs';
import { toolRoutes } from '@/routes/siteMaps';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { ToolAccessDenied } from '@/app/common/components/ToolAccessGuard';
import useToolAccess from '@/app/common/hooks/useToolAccess';
import { NewSerpContext, RemoveSerpContext } from '@/context/Context';
import { useGetStatusSerpQuery } from '@/app/Serp/api/historyApi';
import SerpSocket from './components/common/SerpSocket';
import RouteHelperAlerts from '@/components/common/RouteHelperAlerts';
import DataLoadError from '@/components/errors/DataLoadError';
import {
  shouldShowDataLoadError,
  shouldShowDataLoadErrorFromQueries,
  refetchQueries,
  isAnyQueryFetching,
  isNetworkApiError,
  type ApiQueryError
} from '@/app/common/utils/apiErrorUtils';
import SerpProgressBar from './components/common/SerpProgressBar';
import ToolsBreadcrumb from '../common/components/ToolsBreadcrumb';
import NewSerpForm from './components/common/NewSerpForm';
import NoSerpData from './components/common/NoSerpData';
import ExpireCheck from '../common/components/ExpireCheck';
import useDomain from '@/hooks/useDomain';

type BreadcrumbItem = { text: string; href: string };

type OutletContext = [
  BreadcrumbItem[],
  (items: BreadcrumbItem[]) => void
];

type NavRoute = {
  name: string;
  to: string;
  disabled?: boolean;
  helper?: ReactNode;
  children?: NavRoute[];
};

type NavSection = {
  name: string;
  children?: NavRoute[];
};

const SerpMain = () => {
  const { keyword, slug, loc, lang } = useParams<{
    keyword?: string;
    slug?: string;
    loc?: string;
    lang?: string;
  }>();
  const { domain, domain_slug } = useDomain();
  const access = useToolAccess('serp');
  const [breadcrumbData, setBreadcrumbData] = useState<BreadcrumbItem[]>([]);

  // REMOVE SERP
  const [rem_serp_id, setRem_serp_id] = useState('');
  const [rem_keyword, setRem_keyword] = useState('');
  const [show, setShow] = useState(false);

  // CONTEXT
  const [serpId, setSerpId] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [step, setStep] = useState(1);

  // STATUS
  const {
    data: userInfo,
    isError,
    error,
    refetch,
    isFetching
  } = useGetUserInfoQuery();

  const { data: serpStatus, isFetching: isLoadingStatusSerp } =
    useGetStatusSerpQuery({
      serp_id: slug ? slug : 'last',
      domain_slug,
      requested_user: {
        name_family: `${userInfo?.data?.first_name} ${userInfo?.data?.last_name}`,
        email: userInfo?.data?.email ?? ''
      }
    });

  const routes = (toolRoutes.children as NavSection[]).find(
    item => item.name === 'ابزار SERP'
  );

  const location = useLocation();
  const navItems = routes;

  const tabTo = (route: NavRoute) =>
    route.to.includes('history')
      ? `${route.to}/${domain_slug}/${domain}`
      : `${route.to}/${domain_slug}/${domain}/${slug ? slug : 'last'}/${loc ? loc : 'last'}/${lang ? lang : 'last'}/${keyword ? keyword : 'last'}`;

  const activePage = useMemo(
    () =>
      navItems?.children?.find(route => {
        if (route.disabled) return false;
        const to = tabTo(route);
        return (
          location.pathname === to || location.pathname.startsWith(`${to}/`)
        );
      }),
    // tabTo only reads the deps below
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navItems, location.pathname, domain_slug, domain, slug, loc, lang, keyword]
  );

  if (
    shouldShowDataLoadErrorFromQueries(
      { isError, error },
      {
        isError: access.isError,
        error: access.error as unknown as ApiQueryError
      }
    )
  ) {
    return (
      <DataLoadError
        onRetry={() => refetchQueries(refetch, access.refetch)}
        isRetrying={isAnyQueryFetching({ isFetching }, access)}
        isNetworkError={
          isNetworkApiError(error) ||
          isNetworkApiError(access.error as unknown as ApiQueryError)
        }
      />
    );
  }
  if (access.isLoading || isLoadingStatusSerp) return <></>;

  return (
    <ExpireCheck>
      <NewSerpContext.Provider
        value={{
          isSubmitted,
          setIsSubmitted,
          serpId,
          setSerpId,
          step,
          setStep
        }}
      >
        <RemoveSerpContext.Provider
          value={{
            keyword: rem_keyword,
            setKeyword: setRem_keyword,
            serpId: rem_serp_id,
            setSerpId: setRem_serp_id,
            show,
            setShow
          }}
        >
          <ToolsBreadcrumb
            parentName="ابزار Serp"
            parentUrl={`/serp/history/${domain_slug}/${domain}`}
            childs={breadcrumbData}
          />

          <ToolTabs
            items={(navItems?.children ?? []).map(route => {
              const to = tabTo(route);
              return {
                key: route.to,
                label: route.name,
                to: route.disabled ? '#' : to,
                disabled: route.disabled,
                state: { path: location.pathname, query: null },
                active: activePage?.to === route.to
              };
            })}
          />

          {activePage && <RouteHelperAlerts route={activePage} />}

          {shouldShowDataLoadError(isError, error) ? (
            <DataLoadError
              onRetry={() => refetch()}
              isRetrying={isFetching}
              isNetworkError={isNetworkApiError(error)}
            />
          ) : !access.haveAccess ? (
            <ToolAccessDenied access={access} tool_label="ابزار SERP" />
          ) : (
            <>
              {step !== 1 && <SerpProgressBar />}
              {!isLoadingStatusSerp && (
                <>
                  {serpStatus?.data && keyword && (
                    <NewSerpForm
                      latestKeyword={{
                        keyword: serpStatus?.data?.keyword,
                        location_code: serpStatus?.data?.location_code,
                        language_code: serpStatus?.data?.language_code
                      }}
                    />
                  )}
                  {serpStatus?.data &&
                    location.pathname.includes('history') && (
                      <NewSerpForm
                        latestKeyword={{
                          keyword: '',
                          location_code: 'ir',
                          language_code: 'fa'
                        }}
                      />
                    )}
                  {serpStatus?.data &&
                    !(
                      serpStatus?.data?.completed_serp_step &&
                      serpStatus?.data?.completed_domain_step &&
                      serpStatus?.data?.completed_page_step
                    ) && <SerpSocket serp_id={slug} />}
                  {serpStatus?.data ? (
                    <Outlet
                      context={
                        [
                          breadcrumbData,
                          setBreadcrumbData
                        ] satisfies OutletContext
                      }
                    />
                  ) : (
                    <NoSerpData />
                  )}
                </>
              )}
            </>
          )}
        </RemoveSerpContext.Provider>
      </NewSerpContext.Provider>
    </ExpireCheck>
  );
};

export default SerpMain;
